import { ApolloError, gql, useApolloClient, useMutation } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconOverflow from '@apollo/icons/small/IconOverflow.svg';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  FormLabel,
  Group,
  IconButton,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Form as CustomForm } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { FullNameColumn } from 'src/components/fullNameColumn/FullNameColumn';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useUpgradeContext } from 'src/hooks/useUpgradeContext';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const ApiKeysCard = ({
  setError,
  graph,
  userRoles,
}: {
  setError: (e: ApolloError) => void;
  graph: GraphQLTypes.GraphSettings__APIKeysPage_service;
  userRoles: boolean;
}) => {
  const history = useHistory();
  const location = useLocation();
  const { upgradeRoute } = useUpgradeContext();

  return (
    <Card>
      <CardHeader>
        <CardTitle>API keys</CardTitle>
        <PermissionGuard graphId={graph.id} graphPermissions="canQueryTokens">
          <CardDescription>
            Your API keys are unique to <b>{graph.title}</b>. They are used to
            uniquely identify data from your graph.
          </CardDescription>
        </PermissionGuard>
        <PermissionGuard
          graphId={graph.id}
          graphPermissions="canQueryTokens"
          fallback={null}
        >
          <PermissionWarning
            graphId={graph.id}
            graphPermissions="canManageKeys"
          >
            <Button
              variant="secondary"
              onClick={() => {
                history.push(
                  setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.createGraphApiToken,
                  }),
                );
              }}
            >
              Create new key
            </Button>
          </PermissionWarning>
        </PermissionGuard>
      </CardHeader>
      <CardBody>
        <PermissionGuard
          graphId={graph.id}
          graphPermissions="canQueryTokens"
          fallback={null}
        >
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>
                    <Group>
                      Role
                      <Tooltip
                        interactive
                        label={
                          userRoles ? (
                            <p>
                              You can read about the different roles an API key
                              can have in the{' '}
                              <Link
                                isExternal
                                to={DocsRouter.path('GraphApiKeyRoles')}
                              >
                                Studio documentation.
                              </Link>
                            </p>
                          ) : (
                            <p>
                              To adjust the permissions of your graph's API
                              keys, please{' '}
                              <Link to={upgradeRoute}>
                                upgrade to Enterprise
                              </Link>
                              .
                            </p>
                          )
                        }
                      >
                        <IconInfo className="text-icon-primary" />
                      </Tooltip>
                    </Group>
                  </Th>
                  <Th className="w-px">Created by</Th>
                  <Th>Created at</Th>
                  <Th className="w-px">
                    <span className="sr-only">Actions</span>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {(graph.apiKeys || []).map((apiKey) => {
                  const { createdBy, createdAt, keyName } = apiKey;

                  return (
                    <Tr key={apiKey.id}>
                      <Td>{keyName}</Td>
                      <Td>
                        <Group gap={2}>
                          {Config.roleNameByRoleEnum[apiKey.role]}
                          {apiKey.role ===
                            GraphQLTypes.UserPermission.LEGACY_GRAPH_KEY && (
                            <Tooltip
                              interactive
                              label={
                                <div>
                                  <p>
                                    Before January 2021, graph keys did not have
                                    roles. Keys created before that date (which
                                    are shown as Legacy Admin keys) can perform
                                    the following operations:
                                  </p>
                                  <ul className="ml-2 list-inside list-disc">
                                    <li>Create new variants</li>
                                    <li>Run schema checks</li>
                                    <li>View and manage graph API keys</li>
                                    <li>Push schemas to a graph</li>
                                    <li>
                                      See subgraph schemas in federated graphs
                                    </li>
                                    <li>View graph schemas and changelogs</li>
                                    <li>Modify schema checks configuration</li>
                                  </ul>
                                  <p className="mt-1">
                                    You can no longer create keys with exactly
                                    this permission. New keys must have{' '}
                                    <Link
                                      isExternal
                                      to={DocsRouter.path('GraphApiKeyRoles')}
                                    >
                                      a specific role assigned to them
                                    </Link>{' '}
                                    when they are created.
                                  </p>
                                </div>
                              }
                            >
                              <IconInfo className="flex-shrink-0 text-icon-primary" />
                            </Tooltip>
                          )}
                        </Group>
                      </Td>
                      <Td>
                        {createdBy?.__typename === 'User' &&
                        graph.account?.id ? (
                          <FullNameColumn
                            accountId={graph.account.id}
                            fullName={createdBy.fullName}
                            ghUsername={createdBy.githubUsername}
                            avatarUrl={createdBy.avatarUrl}
                            email={null}
                          />
                        ) : null}
                      </Td>
                      <Td>
                        <RelativeTimestamp value={createdAt} />
                      </Td>
                      <Td isActionsMenu>
                        <UserActionsMenu
                          serviceId={graph.id || ''}
                          apiKey={apiKey}
                          setError={setError}
                        />
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </PermissionGuard>
      </CardBody>
    </Card>
  );
};

const UserActionsMenu = ({
  serviceId,
  apiKey,
  setError,
}: {
  serviceId: string;
  apiKey: Readonly<{ id: string; keyName: string | null }>;
  setError: (e: ApolloError) => void;
}) => {
  const client = useApolloClient();
  const [idOfKeyToRename, setIdOfKeyToRename] = useState<string | null>(null);
  const [renameAPIKey, { loading: renameAPIKeyLoading }] = useMutation<
    GraphQLTypes.RenameAPIKeyMutation,
    GraphQLTypes.RenameAPIKeyMutationVariables
  >(
    gql`
      mutation RenameAPIKeyMutation($serviceId: ID!, $id: ID!, $name: String!) {
        service(id: $serviceId) {
          service {
            id
          }
          renameKey(id: $id, newKeyName: $name) {
            id
            keyName
          }
        }
      }
    `,
    {
      onCompleted: (data) => {
        setIdOfKeyToRename(null);
        client.cache.evict({
          id: client.cache.identify({
            __typename: data.service?.service.__typename,
            id: data.service?.service.id,
          }),
          fieldName: 'apiKeys',
        });
      },
      onError: (e) => setError(e),
    },
  );
  const [idOfKeyToRemove, setIdOfKeyToRemove] = useState<string | null>(null);
  const [removeKeyMutation, { loading: removeLoading }] = useMutation<
    GraphQLTypes.RemoveAPIKeyMutation,
    GraphQLTypes.RemoveAPIKeyMutationVariables
  >(
    gql`
      mutation RemoveAPIKeyMutation($serviceId: ID!, $id: ID!) {
        service(id: $serviceId) {
          service {
            id
          }
          removeKey(id: $id)
        }
      }
    `,
    {
      onCompleted: (data) => {
        client.cache.evict({
          id: client.cache.identify({
            __typename: data.service?.service.__typename,
            id: data.service?.service.id,
          }),
          fieldName: 'apiKeys',
        });

        setIdOfKeyToRemove(null);
      },
      onError: (e) => setError(e),
    },
  );

  return (
    <>
      <Menu placement="bottom-end" autoSelect={false}>
        <MenuButton as={IconButton} variant="secondary">
          <IconOverflow />
        </MenuButton>
        <MenuContent>
          <MenuList>
            <MenuItem
              onClick={() => {
                setIdOfKeyToRename(apiKey.id);
              }}
            >
              Rename Token
            </MenuItem>
            <MenuItem
              className="text-error hover:bg-error hover:text-error"
              onClick={() => {
                setIdOfKeyToRemove(apiKey.id);
              }}
            >
              Delete Token
            </MenuItem>
          </MenuList>
        </MenuContent>
      </Menu>

      {idOfKeyToRename && (
        <Modal
          isOpen
          onClose={() => {
            setIdOfKeyToRename(null);
          }}
          size="xl"
        >
          <ModalOverlay />
          <CustomForm
            onSubmit={(event) => {
              const name = new FormData(event.currentTarget).get('name');

              if (typeof name !== 'string') {
                throw new Error('Could not get name from form');
              }

              renameAPIKey({
                variables: {
                  serviceId,
                  id: idOfKeyToRename,
                  name,
                },
              });
            }}
          >
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Rename API Key</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <FormLabel>New name</FormLabel>
                <Input
                  autoFocus
                  defaultValue={apiKey.keyName || ''}
                  name="name"
                />
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    onClick={() => setIdOfKeyToRename(null)}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    isLoading={renameAPIKeyLoading}
                    loadingText="Rename"
                    type="submit"
                  >
                    Rename
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </CustomForm>
        </Modal>
      )}
      {idOfKeyToRemove && (
        <Modal
          isOpen
          onClose={() => {
            setIdOfKeyToRemove(null);
          }}
          size="xl"
        >
          <ModalOverlay />
          <CustomForm
            noValidate
            onSubmit={() => {
              removeKeyMutation({
                variables: { serviceId, id: idOfKeyToRemove },
              });
            }}
          >
            <ModalContent>
              <ModalHeader>
                <ModalTitle>
                  {apiKey.keyName !== ''
                    ? `Are you sure you want to delete the API Key "${apiKey.keyName}"?`
                    : 'Are you sure you want to delete this unnamed API Key?'}
                </ModalTitle>
              </ModalHeader>
              <ModalBody>
                Deleting this key means any access granted by this key will no
                longer be valid.
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    onClick={() => setIdOfKeyToRemove(null)}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    isDisabled={removeLoading}
                    type="submit"
                  >
                    Yes, delete key
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </CustomForm>
        </Modal>
      )}
    </>
  );
};
